import { Localization } from "@viamap/viamap2-common";
import { useState } from "react";
import { GlassButton } from "./GlassButtons";
import { GlassInputControl, GlassInputControlLabel, GlassInputGroup } from "./GlassInput";
import { message } from "./Message";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import {
  downloadTingbogBilag,
  downloadTingbogDocument,
  getTingbogDownloadFileName,
  parseTingbogAktIdInput,
  parseTingbogBilagIdInput,
} from "src/managers/TingbogDownload";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";

export function FetchTingbogAktDialog({ window }: { window: ManagedWindow }) {
  const { dispatch: windowDispatch } = useWindowContext();
  const [aktId, setAktId] = useState("");
  const [bilagId, setBilagId] = useState("");
  const [busy, setBusy] = useState<"akt" | "bilag" | null>(null);

  async function runDownload(kind: "akt" | "bilag", work: () => Promise<void>) {
    if (busy) {
      return;
    }
    setBusy(kind);
    message.progress(Localization.getText("Downloading..."));
    try {
      await work();
    } finally {
      message.progress();
      setBusy(null);
    }
  }

  async function handleAktDownload() {
    const parsed = parseTingbogAktIdInput(aktId);
    if (!parsed) {
      message.error(Localization.getText("Enter an akt id, for example 51_M_205"));
      return;
    }
    await runDownload("akt", () =>
      downloadTingbogDocument(
        { kind: "akt", value: parsed },
        getTingbogDownloadFileName(`akt_${parsed}`),
      ),
    );
  }

  async function handleBilagDownload() {
    const parsed = parseTingbogBilagIdInput(bilagId);
    if (!parsed) {
      message.error(Localization.getText("Enter a bilag UUID"));
      return;
    }
    await runDownload("bilag", () => downloadTingbogBilag(parsed));
  }

  return (
    <AdvancedDragModal
      window={window}
      variant="Info"
      PosDefault={{ top: "80px", left: "80px", width: "400px" }}
      title={Localization.getText("Fetch Tingbog Akt")}
      onClose={() => windowDispatch(closeWindow(window.id))}
    >
      <ADModalBody>
        <GlassInputGroup autoFocus={0} onSubmit={handleAktDownload} onEscape={() => windowDispatch(closeWindow(window.id))}>
          <GlassInputControlLabel>Akt</GlassInputControlLabel>
          <GlassInputControl>
            <input
              autoFocus
              disabled={busy !== null}
              placeholder="51_M_205"
              value={aktId}
              onChange={(e) => setAktId(e.target.value)}
            />
            <GlassButton disabled={busy !== null} onClick={handleAktDownload}>
              {Localization.getText("Download PDF")}
            </GlassButton>
          </GlassInputControl>
        </GlassInputGroup>
        <GlassInputGroup onSubmit={handleBilagDownload} onEscape={() => windowDispatch(closeWindow(window.id))}>
          <GlassInputControlLabel>{Localization.getText("Bilag UUID")}</GlassInputControlLabel>
          <GlassInputControl>
            <input
              disabled={busy !== null}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={bilagId}
              onChange={(e) => setBilagId(e.target.value)}
            />
            <GlassButton disabled={busy !== null} onClick={handleBilagDownload}>
              {Localization.getText("Download file")}
            </GlassButton>
          </GlassInputControl>
        </GlassInputGroup>
      </ADModalBody>
    </AdvancedDragModal>
  );
}
